import { ROUTES } from './routes';

/** One public marketing-site destination, as the header and footer render it. */
export interface PublicNavLink {
  label: string;
  to: string;
}

// The four public marketing pages, each declared once. The header and footer
// below both compose these, so a label can never be renamed in one place and
// not the other - which is exactly how the footer kept saying "Due Diligence
// Platform" after the header had moved on (FR-305).
//
// Fundraising Support's route is now `/fundraising-support` - renamed as a
// follow-up once the label change shipped (System Designer instruction,
// supersedes A-338's original "route stays" decision). The old
// `/due-diligence-platform` path still resolves: `App.tsx` redirects it to
// `ROUTES.DUE_DILIGENCE_PLATFORM` so no existing link breaks.
// `ROUTES.PARTNERS` (`/partners`) is the public marketing page;
// `ROUTES.TRUSTED_PARTNERS` (`/trusted-partners`) is the unrelated
// authenticated in-app Perks page (A-126).
const BENEFITS: PublicNavLink = { label: 'Benefits', to: ROUTES.BENEFITS };
const FUNDRAISING_SUPPORT: PublicNavLink = {
  label: 'Fundraising Support',
  to: ROUTES.DUE_DILIGENCE_PLATFORM,
};
const INVESTORS: PublicNavLink = { label: 'Investors', to: ROUTES.INVESTORS };
const TRUSTED_PARTNERS: PublicNavLink = { label: 'Trusted Partners', to: ROUTES.PARTNERS };

/**
 * The header nav, in order (FR-305; design §54 D7).
 *
 * There is no "Home" link - the logo is the route home - and Investors
 * precedes Trusted Partners (A-346).
 */
export const PUBLIC_NAV_LINKS: readonly PublicNavLink[] = [
  BENEFITS,
  FUNDRAISING_SUPPORT,
  INVESTORS,
  TRUSTED_PARTNERS,
] as const;

/**
 * The same pages split across the footer's two sub-columns, matching Figma's
 * own footer proportions. The grouping is deliberately the one the footer
 * already shipped (FR-305 changes the label only, not the layout), so it is
 * composed here rather than sliced out of the header order. The footer
 * appends its own "Contact" mailto to the second column; that is not a page,
 * so it is not declared here.
 */
export const FOOTER_NAV_COLUMNS: readonly (readonly PublicNavLink[])[] = [
  [BENEFITS, FUNDRAISING_SUPPORT, TRUSTED_PARTNERS],
  [INVESTORS],
] as const;
